// durableStore — a small IndexedDB key/value store for the handful of values
// that must survive when iOS decides localStorage is disposable (Jill's iPad,
// 2026-07-11: force-closed the app, reopened offline, login token was gone
// while drafts survived). iOS treats IndexedDB as sturdier storage, and the
// navigator.storage.persist() request in main.jsx applies to it.
//
// This is a MIRROR, not a replacement: localStorage stays the fast synchronous
// source of truth; these copies exist to restore from when it loses something.
// Everything here must degrade to "returns null / does nothing" on any error —
// private browsing, quota, corrupt DB — the app must never fail because its
// backup layer did.

const DB_NAME = "seapa-durable";
const STORE = "kv";

// iOS Safari has a long-standing bug where an IndexedDB call after an app
// resume can HANG forever (open() never fires any callback). Nothing here may
// ever block the app on that: every operation races a deadline and falls back
// to "not available". Jill's iPad hit exactly this on 2026-07-11 — the
// Continue-offline button waited on a durableGet that never answered.
const DEADLINE_MS = 1200;
function withDeadline(promise, fallback) {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(fallback), DEADLINE_MS)),
  ]).catch(() => fallback);
}

function openDb() {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) {
          req.result.createObjectStore(STORE);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      req.onblocked = () => reject(new Error("blocked"));
    } catch (e) {
      reject(e);
    }
  });
}

async function durableGetInner(key) {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => { resolve(req.result ?? null); db.close(); };
      req.onerror = () => { resolve(null); db.close(); };
    });
  } catch {
    return null;
  }
}

async function durableSetInner(key, value) {
  try {
    const db = await openDb();
    await new Promise((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => { resolve(); db.close(); };
      tx.onerror = () => { resolve(); db.close(); };
      tx.onabort = () => { resolve(); db.close(); };
    });
  } catch { /* backup layer never throws */ }
}

async function durableEntriesInner() {
  try {
    const db = await openDb();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE, "readonly");
      const store = tx.objectStore(STORE);
      const keysReq = store.getAllKeys();
      const valsReq = store.getAll();
      tx.oncomplete = () => {
        const keys = keysReq.result || [];
        const vals = valsReq.result || [];
        resolve(keys.map((k, i) => [k, vals[i]]));
        db.close();
      };
      tx.onerror = () => { resolve([]); db.close(); };
      tx.onabort = () => { resolve([]); db.close(); };
    });
  } catch {
    return [];
  }
}

async function durableRemoveInner(key) {
  try {
    const db = await openDb();
    await new Promise((resolve) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => { resolve(); db.close(); };
      tx.onerror = () => { resolve(); db.close(); };
      tx.onabort = () => { resolve(); db.close(); };
    });
  } catch { /* ignore */ }
}

export function durableGet(key) {
  return withDeadline(durableGetInner(key), null);
}

export function durableSet(key, value) {
  return withDeadline(durableSetInner(key, value), undefined);
}

export function durableRemove(key) {
  return withDeadline(durableRemoveInner(key), undefined);
}

export function durableEntries() {
  return withDeadline(durableEntriesInner(), []);
}
